<template>
  <div :class="[$style.base, { [$style.hasShadow]: hasShadow }]">
    <transition>
      <div
        :class="$style.back"
        v-show="
          currentStepIndex > 0 && currentStep.name !== 'booking-completed'
        "
        @click="$emit('back')"
        v-test="'back'"
      >
        <svg
          width="8"
          height="13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            :fill="widgetSettings.color"
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.81.53l.586.615c.18.196.29.467.29.746 0 .278-.11.549-.29.738L3.592 6.613l3.804 3.985c.18.188.29.459.29.738 0 .278-.11.549-.29.746l-.587.606a.991.991 0 01-.712.312.96.96 0 01-.704-.312L.297 7.351A1.053 1.053 0 010 6.613c0-.279.11-.55.297-.746L5.393.53a.985.985 0 01.704-.304c.266 0 .524.115.712.304z"
          />
        </svg>
      </div>
    </transition>
    <LanguageSelect v-show="currentStepIndex === 0" />
    <div :class="$style.heading" :style="`color: ${widgetSettings.color}`">
      <div :class="$style.headingInner">
        <transition-group :name="transitionName">
          <div
            :class="$style.headingName"
            v-for="(step, index) in steps"
            :key="index + 0"
            v-show="index === currentStepIndex"
          >
            {{ step.heading }}
          </div>
        </transition-group>
      </div>
    </div>
    <div
      v-if="!widgetSettings.inline"
      :class="$style.close"
      @click="onCloseClick"
    >
      <IconClose />
    </div>
  </div>
</template>

<script setup lang="ts">
import IconClose from '@/components/IconClose.vue';
import { useBookingStore } from '@/stores/booking';
import LanguageSelect from './language-select/index.vue';
import widgetSettings from '@/widget-settings';
import { computed, ref } from 'vue';
import { watch } from 'vue';
import type { Step } from './types';

defineEmits(['back']);
const props = defineProps<{
  currentStep: Step;
  currentStepIndex: number;
  steps: Step[];
}>();

const transitionName = ref('heading-next');
const hasShadow = computed(
  () =>
    props.currentStep.name !== 'choose-services' &&
    props.currentStep.name !== 'booking-completed'
);

watch(
  () => props.currentStepIndex,
  (curValue, prevValue) => {
    transitionName.value =
      curValue < prevValue ? 'heading-prev' : 'heading-next';
  }
);

const onCloseClick = () => {
  const { bookingResponse } = useBookingStore();
  const message =
    (widgetSettings.type === 'giftcard' ? 'voucher' : 'booking') +
    (bookingResponse ? '-remove' : '-close');
  window.parent.postMessage(message, '*');
};
</script>

<style lang="scss" module>
$btn-width: 50px;

.base {
  position: relative;
  z-index: 4;

  &.hasShadow {
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.13);
  }
}
.heading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 $btn-width;
  font-weight: bold;
  text-align: center;
}

.back,
.close {
  position: absolute;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: $btn-width;
  cursor: pointer;
}

.back {
  left: 0;
  transition: opacity 0.15s;

  &:global(.v-enter-from),
  &:global(.v-leave-to) {
    opacity: 0;
  }
}

.close {
  right: 0;
}

.headingInner {
  position: relative;
  width: 100%;
}

.headingName {
  &:global(.heading-next-enter-active),
  &:global(.heading-next-leave-active),
  &:global(.heading-prev-enter-active),
  &:global(.heading-prev-leave-active) {
    transition:
      transform 0.2s $easing,
      opacity 0.2s $easing;
  }

  &:global(.heading-next-leave-to),
  &:global(.heading-next-enter-from),
  &:global(.heading-prev-leave-to),
  &:global(.heading-prev-enter-from) {
    opacity: 0;
  }

  &:global(.heading-next-leave-to),
  &:global(.heading-prev-enter-from) {
    transform: translateY(-18px);
  }

  &:global(.heading-next-enter-from),
  &:global(.heading-prev-leave-to) {
    transform: translateY(18px);
  }

  &:global(.heading-next-enter-active),
  &:global(.heading-prev-enter-active) {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }
}
</style>
